import { FC, useCallback, useMemo, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { SessionListItem } from "@/features/training/ui";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import {
	Box,
	Button,
	ButtonGroup,
	Chip,
	LinearProgress,
	List,
	Paper,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

export type SessionFilter = "all" | "active" | "archived";

interface SessionsListProps {
	entityId: number;
	entityName: string;
	fetchActiveSessions: (
		id: number,
		includeDetails: boolean,
	) => { data?: unknown; isLoading: boolean };
	fetchArchivedSessions: (
		id: number,
		includeDetails: boolean,
	) => { data?: unknown; isLoading: boolean };
	/** Main section title (e.g. "Sessions") */
	sectionTitle?: string;
	activeTitle: string;
	archivedTitle: string;
	basePath: string;
	/** Reduced padding for embedded layouts */
	compact?: boolean;
}

interface SessionListRow {
	Id: number;
	Name: string;
	Date: string | Date;
}

const normalizeSessions = (data: unknown): SessionListRow[] => {
	if (!Array.isArray(data)) {
		return [];
	}

	return data.flatMap((item) => {
		if (typeof item !== "object" || item === null) {
			return [];
		}

		const record = item as Record<string, unknown>;
		const id = typeof record.Id === "number" ? record.Id : null;
		const date = record.Date;

		if (id === null || (typeof date !== "string" && !(date instanceof Date))) {
			return [];
		}

		const name =
			typeof record.Name === "string" && record.Name.trim().length > 0
				? record.Name
				: typeof record.CourseName === "string" &&
					  record.CourseName.trim().length > 0
					? record.CourseName
					: `Session ${id}`;

		return [{ Id: id, Name: name, Date: date }];
	});
};

export const SessionsList: FC<SessionsListProps> = ({
	entityId,
	entityName,
	fetchActiveSessions,
	fetchArchivedSessions,
	sectionTitle = "Training.Session.Sessions",
	activeTitle,
	archivedTitle,
	basePath,
	compact = false,
}) => {
	const theme = useTheme();
	const [filter, setFilter] = useState<SessionFilter>("active");

	const { data: activeSessionsData, isLoading: isLoadingActive } =
		fetchActiveSessions(entityId, true);
	const { data: archivedSessionsData, isLoading: isLoadingArchived } =
		fetchArchivedSessions(entityId, true);

	const isLoading = isLoadingActive || isLoadingArchived;
	const activeSessions = useMemo(
		() => normalizeSessions(activeSessionsData),
		[activeSessionsData],
	);
	const archivedSessions = useMemo(
		() => normalizeSessions(archivedSessionsData),
		[archivedSessionsData],
	);

	const sortedActiveSessions = useMemo(
		() =>
			activeSessions
				.slice()
				.sort(
					(a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime(),
				),
		[activeSessions],
	);

	const sortedArchivedSessions = useMemo(
		() =>
			archivedSessions
				.slice()
				.sort(
					(a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime(),
				),
		[archivedSessions],
	);

	const handleFilterChange = useCallback((f: SessionFilter) => {
		setFilter(f);
	}, []);

	if (isLoading) return <LinearProgress />;

	const hasActiveSessions = Boolean(activeSessions.length);
	const hasArchivedSessions = Boolean(archivedSessions.length);
	const hasAnySessions = hasActiveSessions || hasArchivedSessions;

	const showActive = filter === "all" || filter === "active";
	const showArchived = filter === "all" || filter === "archived";

	return (
		<Box sx={{ pt: compact ? 0 : 3 }}>
			{hasAnySessions && (
				<Paper
					variant="outlined"
					sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}
				>
					<Box
						sx={(t) => ({
							px: 2.25,
							py: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 1.25,
							borderBottom: 1,
							borderColor: alpha(t.palette.divider, 0.85),
							bgcolor:
								t.palette.mode === "dark"
									? alpha(t.palette.common.white, 0.02)
									: "grey.50",
						})}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								variant="caption"
								sx={{
									display: "block",
									mb: 0.05,
									color: "primary.main",
									fontWeight: 700,
									letterSpacing: "0.08em",
									textTransform: "uppercase",
								}}
							>
								Sessions
							</Typography>
							<Typography variant="subtitle1" fontWeight={700}>
								<Translate String={sectionTitle}>Sessions</Translate>
							</Typography>
						</Box>

						<Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
							<Chip
								size="small"
								label={`Active ${activeSessions.length}`}
								sx={{
									height: 26,
									borderRadius: 2,
									bgcolor: alpha(theme.palette.primary.main, 0.08),
									fontWeight: 700,
								}}
							/>
							<Chip
								size="small"
								label={`Archived ${archivedSessions.length}`}
								sx={{
									height: 26,
									borderRadius: 2,
									bgcolor: alpha(theme.palette.info.main, 0.08),
									fontWeight: 700,
								}}
							/>
						</Stack>
					</Box>

					<Box sx={{ p: 2 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								flexWrap: "wrap",
								gap: 2,
								mb: compact ? 1.5 : 2,
							}}
						>
							<ButtonGroup
								size="small"
								variant="outlined"
								sx={{
									"& .MuiButton-root": {
										textTransform: "none",
										fontWeight: 500,
										px: 2,
										borderRadius: 2,
									},
								}}
							>
							<Button
								onClick={() => handleFilterChange("active")}
								variant={filter === "active" ? "contained" : "outlined"}
								color="primary"
								disableElevation
							>
								<Translate String="Training.Session.Filter.Active">
									Active
								</Translate>
							</Button>
							<Button
								onClick={() => handleFilterChange("archived")}
								variant={filter === "archived" ? "contained" : "outlined"}
								color="primary"
								disableElevation
								disabled={!hasArchivedSessions}
							>
								<Translate String="Training.Session.Filter.Archived">
									Archived
								</Translate>
							</Button>
							{hasActiveSessions && hasArchivedSessions && (
								<Button
									onClick={() => handleFilterChange("all")}
									variant={filter === "all" ? "contained" : "outlined"}
									color="primary"
									disableElevation
								>
									<Translate String="Training.Session.Filter.All">
										All
									</Translate>
								</Button>
							)}
							</ButtonGroup>
						</Box>

					{/* Active sessions */}
					{showActive && hasActiveSessions && (
						<Box>
							{filter === "all" && (
								<Typography
									variant="subtitle2"
									color="text.secondary"
									fontWeight={600}
									sx={{
										mb: 1.5,
										textTransform: "uppercase",
										letterSpacing: 0.5,
									}}
								>
									<Translate String={activeTitle}>{activeTitle}</Translate>
								</Typography>
							)}
							<List disablePadding>
								{sortedActiveSessions.map((session) => (
									<SessionListItem
										key={session.Id}
										title={session.Name}
										name={entityName}
										date={session.Date}
										status="active"
										link={`${basePath}/${session.Id}`}
									/>
								))}
							</List>
						</Box>
					)}

					{/* Archived sessions */}
					{showArchived && hasArchivedSessions && (
						<Box sx={{ mt: filter === "all" ? 3 : 0 }}>
							{filter === "all" && (
								<Typography
									variant="subtitle2"
									color="text.secondary"
									fontWeight={600}
									sx={{
										mb: 1.5,
										textTransform: "uppercase",
										letterSpacing: 0.5,
									}}
								>
									<Translate String={archivedTitle}>{archivedTitle}</Translate>
								</Typography>
							)}
							<List disablePadding>
								{sortedArchivedSessions.map((session) => (
									<SessionListItem
										key={session.Id}
										title={session.Name}
										name={entityName}
										date={session.Date}
										status="archived"
										link={`${basePath}/${session.Id}`}
									/>
								))}
							</List>
						</Box>
					)}
					</Box>
				</Paper>
			)}

			{!hasAnySessions && (
				<Paper
					variant="outlined"
					sx={{
						p: 3,
						borderRadius: 2,
						textAlign: "center",
						borderColor: "divider",
						borderStyle: "dashed",
					}}
				>
					<CalendarMonthOutlinedIcon
						sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
					/>
					<Typography variant="body1" color="text.secondary" fontWeight={500}>
						<Translate String="Training.Session.NoSessions">
							No sessions yet.
						</Translate>
					</Typography>
					<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
						<Translate String="Training.Session.NoSessions.Hint">
							Create a session to get started.
						</Translate>
					</Typography>
				</Paper>
			)}
		</Box>
	);
};
